// ============================================================================
// PsyReportSection — ПРОСМОТР ОТЧЁТА методики (встроенно, без окон).
//
// Состояния раздела: формирование (с подсказкой «долго»), ошибка (+ «Повторить»),
// отчёт для методики не найден, пустой результат, строки по отбору. Результат
// рендерится карточками (PsyReportView) — без таблиц, пригодно для телефона.
// ============================================================================

import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PsyReportEntry, PsyReportResult } from '@/lib/psyReports';
import type { PsyFilteredReport } from '@/lib/psyReportsFilter';
import { PsyReportView } from '@/components/psy/PsyReportView';

export interface PsyReportSectionProps {
  entry: PsyReportEntry;
  loading: boolean;
  error: string;
  result: PsyReportResult | null;
  filtered: PsyFilteredReport | null;
  reportSlow: boolean;
  filtersActive: boolean;
  patientId: string;
  from: string;
  to: string;
  onRetry: () => void;
  onResetFilters: () => void;
}

function detailCount(rows: PsyReportResult['rows']): number {
  return rows.filter((r) => r.type !== 'groupHeader' && r.type !== 'groupFooter' && r.type !== 'grandTotal').length;
}

export function PsyReportSection({
  entry, loading, error, result, filtered, reportSlow,
  filtersActive, patientId, from, to, onRetry, onResetFilters,
}: PsyReportSectionProps) {
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Отчёт</span>
        {!loading && !error && filtered && (
          <span className="text-[11px] text-muted-foreground">
            строк: {detailCount(filtered.rows)}
            {filtered.filtered ? ` из ${detailCount(result?.rows || [])}` : ''}
          </span>
        )}
        <Button
          size="sm" variant="outline" className="h-7 px-2 ml-auto"
          onClick={onRetry}
          disabled={loading}
          title="Сформировать отчёт заново"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {!entry.reportId && (
        <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground bg-card">
          Для этой методики отчёт не найден — доступны протоколы и печать ниже.
        </div>
      )}

      {loading && (
        <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Формирование отчёта…
          </div>
          {reportSlow && <div>Ответа долго нет — проверьте подключение к серверу.</div>}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-destructive/40 px-3 py-2 space-y-1.5">
          <div className="text-sm font-medium text-destructive">Отчёт не сформирован</div>
          <div className="text-xs text-muted-foreground break-words">{error}</div>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onRetry}>
            Повторить
          </Button>
        </div>
      )}

      {!loading && !error && filtered && (
        <>
          {filtersActive && (
            <div className="text-[11px] text-muted-foreground">
              {filtered.filtered ? (
                <>
                  {!filtered.patientApplied && patientId && 'В отчёте нет колонки пациента — отбор по пациенту применён к протоколам ниже. '}
                  {!filtered.periodApplied && (from || to) && 'В отчёте нет даты — период применён к протоколам ниже. '}
                  {filtered.totalsHidden && 'Итоги скрыты: применены отборы.'}
                </>
              ) : (
                <>
                  {patientId ? 'В отчёте нет колонки пациента. ' : ''}
                  {(from || to) ? 'В отчёте нет даты. ' : ''}
                  Отборы применены к протоколам ниже.
                </>
              )}
            </div>
          )}
          {filtered.rows.length === 0 ? (
            <div className="rounded-lg border px-3 py-3 text-xs bg-card flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">
                {filtered.filtered ? 'По выбранным отборам в отчёте нет строк.' : 'В отчёте нет данных.'}
              </span>
              {filtered.filtered && (
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onResetFilters}>
                  Сбросить отбор
                </Button>
              )}
            </div>
          ) : (
            <PsyReportView result={{ ...(result as PsyReportResult), rows: filtered.rows }} />
          )}
        </>
      )}
    </section>
  );
}
